import {Link} from '@wandb/weave/common/util/links';
import React from 'react';

// @todo: Migrate to 'Skeleton' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Skeleton} from '../components/Skeleton';
import {useViewPreviewUrlQuery} from '../generated/graphql';
import {DateFormat, format} from '../util/date';
import {
  type ReportMetadata,
  trackOnGalleryPage as track,
  type TrackProps,
} from '../util/gallery';
import {imgSrcWithDefault} from '../util/images';
import * as S from './GalleryPage.styles';

type PostProps = ReportMetadata & {
  showDescription?: boolean;
  link?: string;
  activeTag?: string;
  tagIsCategory?: (tagName: string) => boolean;
  getActiveTagPath?: (tagName: string) => string;
  onClick?: () => void;
  trackProps?: Omit<TrackProps, 'value'>;
  id?: string;
};

const DEFAULT_IMAGE = 'https://wandb.ai/logo.png';
const DEFAULT_TAG_IS_CATEGORY = () => false;

export const Post: React.FC<PostProps> = ({
  displayName,
  authors,
  addedAt,
  tags,
  announcement,
  previewUrl = DEFAULT_IMAGE,
  description,
  showDescription,
  link,
  activeTag,
  tagIsCategory = DEFAULT_TAG_IS_CATEGORY,
  getActiveTagPath,
  onClick,
  trackProps,
  id,
}) => {
  const nonCategoryTags = tags?.filter(t => !tagIsCategory(t.name)) ?? [];

  // Fetch preview URL if we don't have one or if it's the default image
  const shouldFetchPreviewUrl = previewUrl === DEFAULT_IMAGE;
  const {data: previewUrlData, loading: previewUrlLoading} =
    useViewPreviewUrlQuery({
      variables: {id},
      skip: !shouldFetchPreviewUrl || !id,
    });

  const finalPreviewUrl = previewUrlData?.view?.previewUrl || previewUrl;

  const descriptionContent = description;
  const notDescriptionContent = (
    <>
      {authors?.map(({username, name}, i) => {
        const authorDisplayName = name || username;
        return (
          <React.Fragment key={username}>
            {i > 0 && ', '}
            {link != null ? (
              <Link
                to={`/${username}`}
                onClick={() => {
                  if (trackProps != null) {
                    track('clickPostAuthor', {...trackProps, value: username});
                  }
                }}>
                {authorDisplayName}
              </Link>
            ) : (
              <span>{authorDisplayName}</span>
            )}
          </React.Fragment>
        );
      })}
      <S.DotSeparator />
      {addedAt && format(new Date(addedAt), DateFormat.MONTH_DAY_YEAR)}
      {nonCategoryTags.length > 0 && (
        <>
          <S.DotSeparator />
          {nonCategoryTags.map((t, i) => (
            <React.Fragment key={t.id}>
              {i > 0 && ', '}
              {getActiveTagPath != null ? (
                <Link
                  to={{
                    pathname: getActiveTagPath(t.name),
                    search: window.location.search,
                  }}
                  onClick={() => {
                    if (trackProps != null) {
                      track('clickPostTag', {...trackProps, value: t.name});
                    }
                  }}>
                  {t.name}
                </Link>
              ) : (
                <span>{t.name}</span>
              )}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  );

  const content = (
    <S.Post>
      <S.PostTitle>
        {announcement ? (
          <S.PostFlair>Announcement</S.PostFlair>
        ) : (
          tags
            ?.filter(t => tagIsCategory(t.name))
            .map(t =>
              getActiveTagPath != null ? (
                <S.PostFlairLink
                  key={t.id}
                  to={{
                    pathname: getActiveTagPath(t.name),
                    search: window.location.search,
                  }}
                  onClick={() => {
                    if (trackProps != null) {
                      track('clickPostFlair', {...trackProps, value: t.name});
                    }
                  }}>
                  {t.name}
                </S.PostFlairLink>
              ) : (
                <S.PostFlair key={t.id}>{t.name}</S.PostFlair>
              )
            )
        )}
        {displayName}
      </S.PostTitle>
      <S.PostMeta>
        {showDescription ? descriptionContent : notDescriptionContent}
      </S.PostMeta>
      {previewUrlLoading ? (
        <Skeleton
          height={80}
          width={80}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            borderRadius: '4px',
          }}
        />
      ) : (
        <S.PostImage src={imgSrcWithDefault(finalPreviewUrl)} />
      )}
    </S.Post>
  );
  return link != null ? (
    <S.PostWrapperLink to={link} onClick={onClick}>
      {content}
    </S.PostWrapperLink>
  ) : (
    <S.PostWrapper onClick={onClick}>{content}</S.PostWrapper>
  );
};
